import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/utils";

/**
 * Greenhold button hierarchy per UX-DR23:
 *   primary       — one per screen, the most-important action (forest-900)
 *   secondary     — important-but-not-primary (forest-50 panel)
 *   tertiary/text — low-priority + inline links (forest-500 underline-on-hover)
 *   destructive   — irreversible/value-loss (state-error-fill); always paired with confirm modal
 *
 * Sizes: sm (32×) / md (40× default) / lg (48×) — touch-target ≥44px on mobile
 * is enforced via padding when used as a tap target (UX-DR44).
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded font-medium font-body transition-colors duration-fast ease-standard focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        primary:
          "bg-brand-forest-900 text-brand-paper-100 hover:bg-brand-forest-700",
        secondary:
          "bg-brand-forest-50 text-brand-forest-900 hover:bg-brand-forest-100",
        tertiary:
          "bg-transparent text-brand-forest-500 hover:underline underline-offset-4",
        destructive:
          "bg-state-error-fill text-state-error-fg hover:opacity-90",
        // Convenience variants for shadcn idiomatic naming
        ghost: "hover:bg-brand-forest-50 text-brand-forest-900",
        link: "text-brand-forest-500 underline-offset-4 hover:underline",
      },
      size: {
        sm: "h-8 px-3 text-sm",
        md: "h-10 px-4 text-sm",
        lg: "h-12 px-5 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { buttonVariants };
